import axios from 'axios'
import axiosRetry from 'axios-retry'
import store from '@/store'

/**
 * Crea un client axios con interceptor per autenticazione e gestione 401
 *
 * @param {Object} options
 * @param {string} options.baseURL - URL base del servizio
 * @param {Object} options.headers - Headers aggiuntivi
 * @param {Function} options.getAuthHeader - Funzione che ritorna l'header Authorization
 * @param {Function} options.refreshAuth - Funzione async per refreshare l'auth
 * @param {Object} options.retry - Configurazione axios-retry (opzionale)
 */
export function createClient({
    baseURL,
    headers = {},
    getAuthHeader,
    refreshAuth,
    retry = null
}) {
    const instance = axios.create({
        baseURL,
        headers
    })

    if (retry) {
        axiosRetry(instance, {
            retries: retry.retries || 4,
            retryDelay: retry.retryDelay || ((retryNumber) => Math.pow(2, retryNumber) * 1000),
            retryCondition: retry.retryCondition || axiosRetry.isNetworkOrIdempotentRequestError
        })
    }

    // Request interceptor
    instance.interceptors.request.use(config => {
        const authHeader = getAuthHeader()
        if (authHeader) {
            config.headers['Authorization'] = authHeader
        }
        return config
    })

    // Response interceptor: gestisce 401 con retry (senza logout)
    instance.interceptors.response.use(null, async function(error) {
        if (error.response?.status === 401) {
            try {
                await refreshAuth()

                // Retry con nuovo token
                const originalRequest = error.config
                const authHeader = getAuthHeader()
                if (authHeader) {
                    originalRequest.headers['Authorization'] = authHeader
                }
                return instance(originalRequest)
            } catch (refreshError) {
                // Refresh fallito - propaga l'errore, NON fare logout
                return Promise.reject(error)
            }
        }

        return Promise.reject(error)
    })

    return instance
}
